import React, { useState, useMemo, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "motion/react";

import useStudentsPageQueries, {
  useEventStudentsQueries,
} from "../store/students/studentQueries";
import Popup from "../shared/components/Popup/Popup";
import { formatDateToMonthDDYYYYTimeFormat } from "../shared/utils/formatDate";
import StudentsHeader from "./components/StudentsHeader";
import StudentsTabs from "./components/StudentsTabs";
import EventsTabContent from "./components/EventsTabContent";
import StudentsTabContent from "./components/StudentsTabContent";
import closeIcon from "../assets/images/close_icon.svg";
import { useSelector } from "react-redux";
import {
  getAccountType,
  getUserCrnId,
  getUserSchoolId,
} from "../store/auth/selectors";
import { useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import WipeOverlay from "../shared/components/Transitions/WipeOverlay";
import "./Students.css";
const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const Students = () => {
  const [schoolId, setSchoolId] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(1000);
  const [operation, setOperation] = useState("");
  const [allStudents, setAllStudents] = useState([]);
  const [rawStudentDataForExport, setRawStudentDataForExport] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [eventsError, setEventsError] = useState("");
  const [activeTab, setActiveTab] = useState("students");
  const [isWiping, setIsWiping] = useState(false);
  const [wipeDirection, setWipeDirection] = useState("ltr");
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLoader, setEventLoader] = useState({});
  // Operation progress state
  const [isProcessingOperation, setIsProcessingOperation] = useState(false);
  const [operationProgress, setOperationProgress] = useState({
    currentBatch: 0,
    totalBatches: 0,
  });
  const userCrnId = useSelector(getUserCrnId);
  const userRole = useSelector(getAccountType);
  const userSchoolId = useSelector(getUserSchoolId);
  const {
    eventStudents,
    totalCount: eventStudentsTotal,
    attemptedCount: eventAttemptedTotal,
    currentPage: eventCurrentPage,
    itemsPerPage: eventItemsPerPage,
    attemptFilter,
    isLoading: isEventLoading,
    error: eventFetchError,
    handlePageChange: handleEventPageChange,
    handleLimitChange: handleEventItemsPerPageChange,
    handleAttemptFilterChange,
    refetch: refetchEventStudents,
  } = useEventStudentsQueries(
    1,
    100,
    "ALL",
    selectedEvent?.scheduled_events || null
  );

  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    action: "",
    details: "",
  });

  const eventStudentColumns = [
    { key: "name", label: "NAME" },
    { key: "crn_id", label: "CRN ID" },
    { key: "event_id", label: "EVENT ID" },
    { key: "isd_code", label: "ISD CODE" },
    { key: "phone", label: "PHONE NUMBER" },
    { key: "grade", label: "GRADE" },
    { key: "loginId", label: "LOGIN ID" },
    { key: "examName", label: "EXAM NAME" },
    { key: "regSource", label: "REGISTRATION SOURCE" },
    { key: "registeredOn", label: "REGISTERED ON" },
    { key: "attempt", label: "ATTEMPT" },
    { key: "deepLink", label: "DEEPLINK" },
    { key: "certificateLink", label: "CERTIFICATE LINK" },
    { key: "reportLink", label: "REPORT LINK" },
    { key: "basicReportLink", label: "BASIC REPORT LINK" },
  ];

  const studentColumns = [
    { key: "name", label: "NAME" },
    { key: "crn_id", label: "CRN ID" },
    { key: "isd_code", label: "ISD CODE" },
    { key: "phone", label: "PHONE NUMBER" },
    { key: "grade", label: "GRADE" },
    { key: "loginId", label: "LOGIN ID" },
    { key: "regSource", label: "REGISTRATION SOURCE" },
    { key: "registeredOn", label: "REGISTERED ON" },
    { key: "lastLogin", label: "LAST LOGIN" },
    { key: "attempt", label: "ATTEMPT" },
  ];

  const {
    handleGetAllStudentsBySchool,
    handleGetEventsBySchool,
    handleResetPasswordToDefault,
    handleDeleteStudents,
    handleGetSchoolDetailsById,
  } = useStudentsPageQueries();

  useEffect(() => {
    if (activeTab === "events" && events.length > 0 && !selectedEvent) {
      handleEventClick(events[0]);
    }
  }, [activeTab, events, selectedEvent]);

  useEffect(() => {
    let timer;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showNotification]);

  useEffect(() => {
    let timer;
    if (showErrorNotification) {
      timer = setTimeout(() => {
        setShowErrorNotification(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showErrorNotification]);

  const { paginatedStudents, totalStudents } = useMemo(() => {
    if (activeTab === "events") {
      return {
        paginatedStudents: eventStudents,
        totalStudents: eventStudentsTotal,
      };
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const sliced = allStudents.slice(startIndex, endIndex);
      return {
        paginatedStudents: sliced,
        totalStudents: allStudents.length,
      };
    }
  }, [
    activeTab,
    allStudents,
    currentPage,
    itemsPerPage,
    eventStudents,
    eventStudentsTotal,
  ]);

  const fetchStudents = async () => {
    if (!schoolId) return;

    setStudentsError("");
    try {
      const response = await handleGetAllStudentsBySchool({
        schoolIds: [schoolId],
      });

      if (response?.hits?.hits && response.hits.hits.length > 0) {
        const hits = response.hits.hits;
        const formattedStudents = hits.map((hit) => ({
          id: hit._id,
          name:
            hit._source.first_name +
            (hit._source.last_name ? ` ${hit._source.last_name}` : ""),
          crn_id: hit._source.crn_id || "",
          isd_code: hit._source.isd_code || "",
          phone: hit._source.phone || "",
          grade: hit._source.grade_id || "",
          loginId: hit._source.admission_id || "",
          regSource: hit._source.registration_source || "",
          registeredOn:
            formatDateToMonthDDYYYYTimeFormat(hit._source.created_at) || "",
          lastLogin:
            formatDateToMonthDDYYYYTimeFormat(hit._source.last_active_at) || "",
          attempt:
            hit._source.exam_attempted !== undefined
              ? hit._source.exam_attempted
                ? "Yes"
                : "No"
              : "",
        }));
        setAllStudents(formattedStudents);
        setRawStudentDataForExport(hits.map((hit) => hit._source));
      } else {
        setAllStudents([]);
        setRawStudentDataForExport([]);
        setStudentsError("No students found for this school ID");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudentsError(error.message);
      setAllStudents([]);
      setRawStudentDataForExport([]);
    }
  };

  const fetchEvents = async () => {
    if (!schoolId) return;

    setEventsError("");
    try {
      const response = await handleGetEventsBySchool(schoolId);
      if (response?.data && response.data.length > 0) {
        setEvents(response.data);
      } else {
        setEvents([]);
        setEventsError("No events found for this school ID");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setEventsError(error.message);
      setEvents([]);
    }
  };

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const schoolIdParam = params.get("schoolId");
    if (schoolIdParam && schoolIdParam !== schoolId) {
      setSchoolId(schoolIdParam);
      setActiveTab("students");
    }
  }, [location]);

  // Autofill for School Admin
  useEffect(() => {
    if (userRole === "School Admin" && !schoolId && userSchoolId) {
      setSchoolId(userSchoolId.toUpperCase());
      setActiveTab("students");
    }
  }, [userRole, userSchoolId, schoolId]);

  useEffect(() => {
    if (schoolId) {
      handleGetData();
    }
  }, [schoolId]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSelectedStudents([]);
    setEventLoader((prev) => ({ ...prev, [event.event]: true }));
    // The query will automatically run when selectedEvent changes
    setEventLoader((prev) => ({ ...prev, [event.event]: false }));
  };

  const handleSchoolIdChange = (e) => {
    const newSchoolId = e.target.value.toUpperCase();
    if (newSchoolId !== schoolId) {
      setSchoolId(newSchoolId);
      setSelectedStudents([]);
    }
  };

  const handleGetData = useCallback(async () => {
    if (!schoolId) {
      setStudentsError("Please enter a School ID.");
      setEventsError("Please enter a School ID.");
      return;
    }

    setIsLoading(true);
    setEvents([]);
    setAllStudents([]);
    setRawStudentDataForExport([]);
    setSelectedEvent(null);
    setSchoolDetails(null);
    setIsUserAuthorized(false); // Reset authorization state on new search

    try {
      // 1. Fetch school details
      const schoolDetailsResponse = await handleGetSchoolDetailsById(schoolId);
      if (!schoolDetailsResponse?.data) {
        setStudentsError("School not found.");
        setEventsError("School not found.");
        return;
      }

      const privilegedRoles = ["Super Admin", "MIS Team Admin", "School Admin"];
      const isPrivilegedUser = privilegedRoles.includes(userRole);

      if (!isPrivilegedUser) {
        const pocCrnIds = schoolDetailsResponse.data.poc_array || [];
        const isAuthorized = pocCrnIds.includes(userCrnId);

        if (!isAuthorized) {
          setStudentsError(
            "Access denied: Your CRN ID is not authorized for this school."
          );
          setEventsError(
            "Access denied: Your CRN ID is not authorized for this school."
          );
          return;
        }
      }

      setIsUserAuthorized(true); // Mark user as authorized
      setSchoolDetails(schoolDetailsResponse.data);

      await Promise.all([fetchStudents(), fetchEvents()]);
    } catch (error) {
      console.error("Error in handleGetData:", error);
      setStudentsError("Failed to fetch data. Please try again.");
      setEventsError("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [schoolId, userRole, userCrnId]);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSelectAll = (e) => {
    const sourceStudents =
      activeTab === "events" ? paginatedStudents : allStudents;

    if (e.target.checked) {
      setSelectedStudents(sourceStudents.map((student) => student.loginId));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleStudentSelect = (loginId) => {
    setSelectedStudents((prev) =>
      prev.includes(loginId)
        ? prev.filter((id) => id !== loginId)
        : [...prev, loginId]
    );
  };

  const handleOperationChange = (e) => {
    const selectedOperation = e.target.value;
    if (selectedOperation && selectedStudents.length > 0) {
      setOperation(selectedOperation);
      setShowConfirmation(true);
    }
  };

  const handleShowNotification = (action, details) => {
    setNotificationData({ action, details });
    setShowNotification(true);
  };

  const handleShowErrorNotification = (action) => {
    setNotificationData({
      action,
      details: "Operation failed, please try again later",
    });
    setShowErrorNotification(true);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  const handleTabChange = useCallback(
    (nextTab) => {
      if (nextTab === activeTab || isWiping) {
        return;
      }

      const direction =
        activeTab === "students" && nextTab === "events" ? "ltr" : "rtl";

      setWipeDirection(direction);
      setIsWiping(true);
      setActiveTab(nextTab);
    },
    [activeTab, isWiping]
  );

  const handleConfirmOperation = async () => {
    setIsProcessingOperation(true);
    try {
      if (operation === "reset") {
        await handleResetPasswordToDefault(selectedStudents);
        setIsProcessingOperation(false);
        handleShowNotification(
          "Password Reset",
          `Successfully reset passwords for ${selectedStudents.length} student${
            selectedStudents.length > 1 ? "s" : ""
          }`
        );
        await handleGetData();
      } else if (operation === "delete") {
        // Only delete first 500 students
        const studentsToDelete = selectedStudents.slice(0, 500);
        const actualDeletedCount = studentsToDelete.length;

        await handleDeleteStudents(studentsToDelete);
        setIsProcessingOperation(false);

        handleShowNotification(
          "Delete",
          `Successfully deleted ${actualDeletedCount} student${
            actualDeletedCount > 1 ? "s" : ""
          }`
        );

        setTimeout(() => {
          handleGetData();
        }, 3000);
      }
    } catch (error) {
      console.error("Error performing operation:", error);
      setIsProcessingOperation(false);
      handleShowErrorNotification(
        operation === "reset" ? "Password Reset" : "Delete"
      );
    } finally {
      setShowConfirmation(false);
      setOperation("");
      setSelectedStudents([]);
      setOperationProgress({ currentBatch: 0, totalBatches: 0 });
    }
  };

  const handleCancelOperation = () => {
    setShowConfirmation(false);
    setOperation("");
  };

  const generateStudentCSV = (students, currentSchoolId) => {
    const headers = [
      "first_name",
      "last_name",
      "grade",
      "isd_code",
      "phone",
      "school_id",
      "admission_id",
    ];
    const csvRows = [headers.join(",")];

    const escapeCSVField = (field) => {
      const value = String(field || "");
      // Only quote if the field contains comma, newline, or quote
      if (value.includes(",") || value.includes("\n") || value.includes('"')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    students.forEach((student) => {
      const row = [
        escapeCSVField(student.first_name),
        escapeCSVField(student.last_name),
        escapeCSVField(student.grade_id),
        escapeCSVField(student.isd_code),
        escapeCSVField(student.phone),
        escapeCSVField(currentSchoolId),
        escapeCSVField(student.admission_id),
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  const handleExportStudentsCSV = async () => {
    if (rawStudentDataForExport.length === 0 || !schoolId) {
      console.warn("No student data to export or school ID missing.");
      return;
    }
    setIsExporting(true);

    // Simulate a delay for the animation
    setTimeout(() => {
      try {
        const csvData = generateStudentCSV(rawStudentDataForExport, schoolId);
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `students_export_${schoolId}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error exporting students to CSV:", error);
      } finally {
        setIsExporting(false);
      }
    }, 1500);
  };

  const getConfirmationMessage = () => {
    const count = selectedStudents.length;
    if (operation === "reset") {
      return `Do you really want to reset password for ${count} selected student${
        count > 1 ? "s" : ""
      }?`;
    }
    if (count > 500) {
      return (
        <>
          <p style={{ marginBottom: "12px" }}>
            Do you really want to delete 500 selected students?
          </p>
          <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
            Delete operation supports only 500 users at a time. Please repeat
            the action to delete more users.
          </p>
        </>
      );
    }
    return `Do you really want to delete ${count} selected student${
      count > 1 ? "s" : ""
    }?`;
  };

  const renderPopupContent = () => {
    if (isProcessingOperation) {
      if (operation === "reset") {
        // Simple spinner for reset operation
        return (
          <>
            <CircularProgress size={48} sx={{ color: "#007bff" }} />
            <p style={{ margin: 0, color: "#273743", fontSize: "16px" }}>
              Resetting passwords...
            </p>
          </>
        );
      } else if (operation === "delete") {
        return (
          <>
            <CircularProgress size={48} sx={{ color: "#007bff" }} />
            <p style={{ margin: 0, color: "#273743", fontSize: "16px" }}>
              Deleting students...
            </p>
          </>
        );
      }
    }
    const message = getConfirmationMessage();
    // If message is a string, wrap it in <p>, otherwise return as-is (JSX)
    return typeof message === "string" ? <p>{message}</p> : message;
  };

  return (
    <div className="students-container">
      <StudentsHeader
        schoolId={schoolId}
        handleSchoolIdChange={handleSchoolIdChange}
        handleGetData={handleGetData}
        isLoading={isLoading}
        activeTab={activeTab}
        schoolDetails={schoolDetails}
        isUserAuthorized={isUserAuthorized}
        onUploadSuccess={handleGetData}
      />

      <motion.div layout>
        <StudentsTabs activeTab={activeTab} setActiveTab={handleTabChange} />
      </motion.div>

      <div className="students-tab-content-area">
        {isWiping && (
          <WipeOverlay
            direction={wipeDirection}
            onComplete={() => setIsWiping(false)}
          />
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="students-tab-content-pane"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === "events" ? (
              <EventsTabContent
                events={events}
                onEventClick={handleEventClick}
                eventLoader={eventLoader}
                selectedEvent={selectedEvent}
                error={eventsError || eventFetchError}
                isLoading={isLoading || isEventLoading}
                eventStudentsTotal={eventStudentsTotal}
                eventAttemptedTotal={eventAttemptedTotal}
                eventCurrentPage={eventCurrentPage}
                eventItemsPerPage={eventItemsPerPage}
                onEventPageChange={handleEventPageChange}
                onEventItemsPerPageChange={handleEventItemsPerPageChange}
                paginatedStudents={paginatedStudents}
                columns={eventStudentColumns}
                selectedStudents={selectedStudents}
                onSelectAll={handleSelectAll}
                onSelectRow={handleStudentSelect}
                schoolId={schoolId}
                attemptFilter={attemptFilter}
                onAttemptFilterChange={handleAttemptFilterChange}
                refetchEventStudents={refetchEventStudents}
                isFetchingEventStudents={isEventLoading}
                schoolDetails={schoolDetails}
              />
            ) : (
              <StudentsTabContent
                error={studentsError}
                isLoading={isLoading}
                paginatedStudents={paginatedStudents}
                totalStudents={totalStudents}
                operation={operation}
                selectedStudents={selectedStudents}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onOperationChange={handleOperationChange}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                onSelectAll={handleSelectAll}
                onSelectRow={handleStudentSelect}
                columns={studentColumns}
                schoolId={schoolId}
                onExportCSV={handleExportStudentsCSV}
                isExporting={isExporting}
                canExport={rawStudentDataForExport.length > 0 && !!schoolId}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <Popup
        isVisible={showConfirmation}
        onClose={isProcessingOperation ? undefined : handleCancelOperation}
        title={isProcessingOperation ? "Processing..." : "Warning!"}
        handelPrimery={
          isProcessingOperation ? undefined : handleConfirmOperation
        }
        handelSecondry={
          isProcessingOperation ? undefined : handleCancelOperation
        }
        FirstBtnTitle={isProcessingOperation ? null : "No"}
        SecondBtnTitle={isProcessingOperation ? null : "Yes"}
        isWarning={!isProcessingOperation}
        showSubtitle={!isProcessingOperation}
        contentClassName="warning-popup__content"
      >
        {renderPopupContent()}
      </Popup>

      {showNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #008000",
              zIndex: 1000,
            }}
          >
            <button
              onClick={handleCloseNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#008000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              {notificationData.action} Successful
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
              }}
            >
              {notificationData.details}
            </p>
          </div>
        </NotificationPortal>
      )}

      {showErrorNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #ff0000",
              zIndex: 1000,
            }}
          >
            <button
              onClick={handleCloseErrorNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#ff0000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              {notificationData.action} Failed
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
              }}
            >
              {notificationData.details}
            </p>
          </div>
        </NotificationPortal>
      )}
    </div>
  );
};

export default Students;
